import Plan from "../../../model/Plan";
import Model from "../../../model/Model";
import PlanChange, {PlanChangeType} from "../../../model/PlanChange";
import {useEffect, useState} from "react";
import Person from "../../../model/Person";
import SocialSecurityStrategyChange, {
  ISocialSecurityBenefit,
  ISocialSecurityStrategyChange
} from "../../../model/changes/SocialSecurityStrategyChange";
import Income, {IncomeType} from "../../../model/Income";
import {
  ageToStringFormat, dateToLocalFormat, formatAge,
  getISODateFromDate, getISODateTime, isoToLocalDate, isoToLocalDateString,
  moneyToNumberFormat,
  numberToMoneyFormat, numberToPercentFormat, numberToStringFormat, percentToNumberFormat,
} from "../../../stores/StoreUtilities";
import {useResources} from "../../../stores/ResourceProvider";
import {
  Box,
  DialogContentText,
  FormControlLabel, FormLabel,
  Grid,
  Link,
  Switch, useTheme
} from "@mui/material";
import {addMonths, addYears, differenceInMonths} from "date-fns";
import * as React from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreatePlanChangeInput, UpdatePlanChangeInput} from "../../../API";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Tracking from "../../../components/Tracking";
import LaunchIcon from '@mui/icons-material/Launch';
import SocialSecurityCalc from "../../../components/calculator/SocialSecurityCalc";
import SocialSecurityWidget from "./charts/SocialSecurityWidget";
import Slider from "@mui/material/Slider";
import useDebounce from "../../../components/useDebounce";

const SocialSecurityStrategyDialog = ({
  open,
  plan,
  model,
  onClose,
  onSave
}: {
  open? : boolean
  plan: Plan
  model: Model
  onClose?(): void
  onSave?(plan: Plan, update: PlanChange): void
}) => {
  const [planModel, setPlanModel] = useState<Plan>(plan)
  const [isOpen, setIsOpen] = useState<boolean>(open === true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [planChange, setPlanChange] = useState<SocialSecurityStrategyChange | undefined>()
  const [person1, setPerson1] = useState<Person | undefined>()
  const [person2, setPerson2] = useState<Person | undefined>()

  const [age1, setAge1] = useState<number | undefined>()
  const [defaultAge1, setDefaultAge1] = useState<number | undefined>()
  const [startDate1, setStartDate1] = useState<string | undefined>()
  const [minStartDate1, setMinStartDate1] = useState<Date | undefined>()
  const [amount1, setAmount1] = useState<string | undefined>()
  const [amountHelp1, setAmountHelp1] = useState<string | undefined>()
  const [amountDisable1, setAmountDisable1] = useState<boolean>(false)
  const [fullRetirementBenefit1, setFullRetirementBenefit1] = useState<string | undefined>()
  const [fullRetirementAge1, setFullRetirementAge1] = useState<number>(67)
  const [spousalStart1, setSpousalStart1] = useState<string | undefined>()
  const [spousalBenefit1, setSpousalBenefit1] = useState<string | undefined>()

  const [age2, setAge2] = useState<number | undefined>()
  const [defaultAge2, setDefaultAge2] = useState<number | undefined>()
  const [startDate2, setStartDate2] = useState<string | undefined>()
  const [minStartDate2, setMinStartDate2] = useState<Date | undefined>()
  const [amount2, setAmount2] = useState<string | undefined>()
  const [amountHelp2, setAmountHelp2] = useState<string | undefined>()
  const [amountDisable2, setAmountDisable2] = useState<boolean>(false)
  const [fullRetirementBenefit2, setFullRetirementBenefit2] = useState<string | undefined>()
  const [fullRetirementAge2, setFullRetirementAge2] = useState<number>(67)
  const [spousalStart2, setSpousalStart2] = useState<string | undefined>()
  const [spousalBenefit2, setSpousalBenefit2] = useState<string | undefined>()

  const [persons, setPersons] = useState<Person[]>([])
  const [benefits, setBenefits] = useState<ISocialSecurityBenefit[]>([])

  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [applyReduction, setApplyReduction] = useState<boolean>(false)
  const [commitChanges, setCommitChanges] = useState<boolean>(false)

  const [netLiquidAssets, setNetLiquidAssets] = useState<number>(0)
  const [totalTax, setTotalTax] = useState<number>(0)
  const [isMaximizing, setIsMaximizing] = useState<boolean>(false)

  const { modelStore, calculator, notify } = useResources()

  const theme = useTheme()
  const styles = {
    link: {
      fontSize: 16,
      textDecoration: "none"
    },
    slider: {
      // marginLeft: theme.spacing(1),
      // paddingRight: theme.spacing(-4)
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
  }

  useEffect(() => {
    setPlanModel(plan)
    setIsOpen(open === true)
    let change: SocialSecurityStrategyChange = plan.getChange(PlanChangeType.SocialSecurityStrategy) as SocialSecurityStrategyChange
    if (!change) {
      change = new SocialSecurityStrategyChange({
        accountId: plan.accountId,
        userId: model.userId,
        modelId: model.id,
        planId: plan.id,
        changeType: PlanChangeType.SocialSecurityStrategy,
        name: "Social Security Strategy",
        description: "",
        enabled: true,
      })
    }
    setPlanChange(change)
    if (model.person1) {
      const p1 = model.person1
      const timeline = calculator.personTimeline(p1)
      let startDate
      let amount
      let infRate
      setPerson1(p1)
      setFullRetirementAge1(timeline?.fullSocialSecurityAge ?? 67)
      const benefit1 = change.benefits && change.benefits.length > 0 ? change.benefits[0] : undefined
      if (benefit1) {
        if (benefit1.personalStart) {
          startDate = benefit1.personalStart
          setStartDate1(startDate)
          setFullRetirementBenefit1(numberToMoneyFormat(benefit1.fullBenefit, 0))
        }
        if (benefit1.personalBenefit !== undefined) {
          amount = benefit1.personalBenefit
          setAmount1(numberToMoneyFormat(amount, 0))
          setFullRetirementBenefit1(numberToMoneyFormat(benefit1.fullBenefit, 0))
        }
        if (benefit1.spousalStart) {
          setSpousalStart1(benefit1.spousalStart)
        }
        if (benefit1.spousalBenefit !== 0) {
          setSpousalBenefit1(numberToMoneyFormat(benefit1.spousalBenefit, 0))
        }
        infRate = benefit1.annualInf
      }

      // Get defaults
      if (!startDate || !amount) {
        const income = getSocialSecurityIncome(p1)
        if (income && income.startDate) {
          startDate = getISODateFromDate(income.startDate)
          setStartDate1(startDate)
          setAmount1(numberToMoneyFormat(income.amount,0))
          infRate = income.annualInf
        } else {
          if (timeline?.retireDate) {
            startDate = getISODateFromDate(timeline.retireDate)
            setStartDate1(startDate)
          } else if (timeline?.fullSocialSecurityDate) {
            // Default to full retirement age
            startDate = getISODateFromDate(timeline.fullSocialSecurityDate)
            setStartDate1(startDate)
          }
        }
      }

      if (startDate) {
        setAge1(p1.getAgeOnDate(isoToLocalDate(startDate)))
      }

      const minRetireDate = addMonths(p1.getDateOnAge(62), 1)
      setMinStartDate1(minRetireDate)

      if (infRate !== undefined) {
        setAnnualInf(numberToPercentFormat(infRate ?? 0, 2))
      } else {
        setAnnualInf(numberToPercentFormat(model.getDefaultInflationRate("Income"), 2))
      }
    }

    if (model.person2) {
      const p2 = model.person2
      const timeline = calculator.personTimeline(p2)
      let startDate
      let amount
      setPerson2(p2)
      setFullRetirementAge2(timeline?.fullSocialSecurityAge ?? 67)

      const benefit2 = change.benefits && change.benefits.length > 1 ? change.benefits[1] : undefined
      if (benefit2) {
        if (benefit2.personalStart) {
          startDate = benefit2.personalStart
          setStartDate2(startDate)
          setAge2(p2.getAgeOnDate(isoToLocalDate(startDate)))
          setFullRetirementBenefit2(numberToMoneyFormat(benefit2.fullBenefit, 0))
        }
        if (benefit2.personalBenefit !== undefined) {
          amount = benefit2.personalBenefit
          setAmount2(numberToMoneyFormat(amount, 0))
        }
        if (benefit2.spousalStart) {
          setSpousalStart2(benefit2.spousalStart)
        }
        if (benefit2.spousalBenefit !== 0) {
          setSpousalBenefit2(numberToMoneyFormat(benefit2.spousalBenefit, 0))
        }
      }

      // Get defaults
      if (!startDate || !amount) {
        const income = getSocialSecurityIncome(p2)
        if (income && income.startDate) {
          startDate = getISODateFromDate(income.startDate)
          setStartDate2(startDate)
          setAmount2(numberToMoneyFormat(income.amount, 0))
        } else {
          if (timeline?.retireDate) {
            startDate = getISODateFromDate(timeline.retireDate)
            setStartDate2(startDate)
          } else if (timeline?.fullSocialSecurityDate) {
            // Default to full retirement age
            startDate = getISODateFromDate(timeline.fullSocialSecurityDate)
            setStartDate2(startDate)
          }
        }
      }

      if (startDate) {
        setAge2(p2.getAgeOnDate(isoToLocalDate(startDate)))
      }
      const minRetireDate = addMonths(p2.getDateOnAge(62), 1)
      setMinStartDate2(minRetireDate)
    }

    setPersons(model.persons)
    setApplyReduction(change.applyReduction ?? false)
  }, [plan])

  useEffect(() => {
    if (persons.length > 0) {
      calculateBenefits()
    }
  }, [persons])

  useEffect(() => {
    if (!isLoading) {
      calculateBenefits()
    }
  }, [fullRetirementBenefit1, startDate1, fullRetirementBenefit2, startDate2, isLoading] )
  
  const calculateBenefits = () => {
    const benes: ISocialSecurityBenefit[] = []
    let birthDate1: Date | undefined
    let start1: Date | undefined
    let full1 = 0
    if (person1 && startDate1) {
      birthDate1 = isoToLocalDate(person1.birthDate)
      start1 = isoToLocalDate(startDate1)
      if (start1) {
        setAge1(person1.getAgeOnDate(start1))
      }
      full1 = moneyToNumberFormat(fullRetirementBenefit1)
    }
    let birthDate2: Date | undefined
    let start2: Date | undefined
    let full2 = 0
    if (person2 && startDate2) {
      birthDate2 = isoToLocalDate(person2.birthDate)
      start2 = isoToLocalDate(startDate2)
      if (start2) {
        setAge2(person2.getAgeOnDate(start2))
      }
      full2 = moneyToNumberFormat(fullRetirementBenefit2)
    }

    if (birthDate1 && start1) {
      const benefits = SocialSecurityCalc.calculateRetirementBenefits(birthDate1, start1, full1, birthDate2, start2, full2)
      if (benefits.myStart) {
        setAmount1(numberToMoneyFormat(benefits.myBenefit, 0))
        setAmountDisable1(false)
        if (benefits.spousalStart) {
          setAmountHelp1(benefits.myDescription)
          setSpousalStart1(getISODateFromDate(benefits.spousalStart))
          setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
        } else {
          setAmountHelp1(benefits.myDescription)
          setSpousalStart1(undefined)
          setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
        }
      } else if (benefits.spousalStart) {
        setAmount1(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable1(true)
        setAmountHelp1(benefits.myDescription)
        setSpousalStart1(getISODateFromDate(benefits.spousalStart))
        setSpousalBenefit1(numberToMoneyFormat(benefits.spousalBenefit, 0))
      }
      benes.push({
        personId: person1!.id,
        fullBenefit: full1,
        personalStart: benefits.myStart ? getISODateFromDate(benefits.myStart) : undefined,
        personalBenefit: benefits.myBenefit,
        personalPercent: benefits.myPercent,
        spousalStart: benefits.spousalStart ? getISODateFromDate(benefits.spousalStart) : undefined,
        spousalBenefit: benefits.spousalBenefit,
        spousalPercent: benefits.spousalPercent,
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    } else if (birthDate1 && ((startDate1 && amount1) || (spousalStart1 && spousalBenefit1))) {
      benes.push({
        personId: person1!.id,
        fullBenefit: 0,
        personalStart: startDate1,
        personalBenefit: moneyToNumberFormat(amount1, 0),
        spousalStart: spousalStart1,
        spousalBenefit: moneyToNumberFormat(spousalBenefit1, 0),
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    }

    if (birthDate2 && start2) {
      const benefits = SocialSecurityCalc.calculateRetirementBenefits(birthDate2, start2, full2, birthDate1, start1, full1)
      if (benefits.myStart) {
        setAmount2(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable2(false)
        if (benefits.spousalStart) {
          setAmountHelp2(benefits.myDescription)
          setSpousalStart2(getISODateFromDate(benefits.spousalStart))
          setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
        } else {
          setAmountHelp2(benefits.myDescription)
          setSpousalStart2(undefined)
          setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
        }
      } else if (benefits.spousalStart) {
        setAmount2(numberToMoneyFormat(benefits.myBenefit,0))
        setAmountDisable2(true)
        setAmountHelp2(benefits.myDescription)
        setSpousalStart2(getISODateFromDate(benefits.spousalStart))
        setSpousalBenefit2(numberToMoneyFormat(benefits.spousalBenefit, 0))
      }
      benes.push({
        personId: person2!.id,
        fullBenefit: full2,
        personalStart: benefits.myStart ? getISODateFromDate(benefits.myStart) : undefined,
        personalBenefit: benefits.myBenefit,
        personalPercent: benefits.myPercent,
        spousalStart: benefits.spousalStart ? getISODateFromDate(benefits.spousalStart) : undefined,
        spousalBenefit: benefits.spousalBenefit,
        spousalPercent: benefits.spousalPercent,
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    } else if (birthDate2 && ((startDate2 && amount2) || (spousalStart2 && spousalBenefit2))) {
      benes.push({
        personId: person2!.id,
        fullBenefit: 0,
        personalStart: startDate2,
        personalBenefit: moneyToNumberFormat(amount2, 0),
        spousalStart: spousalStart2,
        spousalBenefit: moneyToNumberFormat(spousalBenefit2, 0),
        annualInf: percentToNumberFormat(annualInf, 2)
      })
    }
    setBenefits(benes)
    setTotalTax(0)
    setNetLiquidAssets(0)
    debounceCalculateFuture(benes)
    setIsLoading(false)
  }

  const calculateFuture = async (benefits: ISocialSecurityBenefit[]) => {
    console.debug(`calculateFuture: ${(new Date()).toISOString()}`)
    if (planChange && model.appliedPlan) {
      // Recalc future
      const change = new SocialSecurityStrategyChange({
        id: planChange.id,
        accountId: planChange.accountId,
        userId: planChange.userId,
        modelId: planChange.modelId,
        planId: planChange.planId,
        changeType: planChange.changeType,
        enabled: true,
        details: JSON.stringify({benefits: benefits, applyReduction: applyReduction})
      })
      const endYear = person1?.latestLifeExpectancyYear
      const modelCopy = await modelStore.getModel(model.id, false, true)
      if (modelCopy) {
        const planCopy = modelCopy.findPlan(plan.id)
        if (planCopy) {
          planCopy.setChange(change)
          planCopy.applyChanges(modelCopy)
          modelCopy.updatedAt = getISODateTime()
          const futureCalc = calculator.calculateFuture(modelCopy, "Social Secrurity Strategy", endYear)
          setNetLiquidAssets(futureCalc.netLiquidAssets)
          setTotalTax(futureCalc.totalTax)
        }
      }
    }
  }

  const debounceCalculateFuture = useDebounce((benefits: ISocialSecurityBenefit[]) => calculateFuture(benefits), 200)

  const maximizeBenefits = () => {
    setIsMaximizing(true)
    // if (person1) {
    //   setStartDate1(getISODateFromDate(person1.getDateOnAge(62)))
    // }
    // if (person2) {
    //   setStartDate2(getISODateFromDate(person2.getDateOnAge(62)))
    // }
    let timer: any
    timer = setTimeout(() => {
      const maxBenefits = SocialSecurityCalc.maximizeRetirementBenefits(persons, moneyToNumberFormat(fullRetirementBenefit1, 0), percentToNumberFormat(annualInf, 0), moneyToNumberFormat(fullRetirementBenefit2, 0))
      if (maxBenefits) {
        console.log(`Max Benefits:`, maxBenefits)
        setStartDate1(maxBenefits[0].personalStart ?? maxBenefits[0].spousalStart)
        if (maxBenefits.length > 1) {
          setStartDate2(maxBenefits[1].personalStart ?? maxBenefits[1].spousalStart)
        }
      }
      setIsMaximizing(false)
      clearTimeout(timer);
    }, 200);
  }

  const getSocialSecurityIncome = (person: Person): Income | undefined => {
    const income = model.incomes.find((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === person.id && !i.archived)
    return income
  }

  // const getOptions = (timeline: IPersonTimeline): Milestone[] => {
  //   const ages = [62, 63, 64, 65, 66, 67, 68, 69, 70]
  //   const options: Milestone[] = ages.map((age: number) => {
  //     const birthDay = timeline.birthDate.getDay()
  //     let label
  //     if (age === timeline.earlySocialSecurityAge) {
  //       if (birthDay <= 2) {
  //         label = `${age} (Early Social Security)`
  //       } else {
  //         label = `${age}+1mo (Early Social Security)`
  //       }
  //     } else if (age === timeline.fullSocialSecurityAge) {
  //       label = `${age} (Full Social Security)`
  //     } else if (age === timeline.delayedSocialSecurityAge) {
  //       label = `${age} (Delayed Social Security)`
  //     } else {
  //       label = String(age)
  //     }
  //     let date = addYears(timeline.birthDate, age)
  //     if (age === timeline.earlySocialSecurityAge && birthDay > 2) {
  //       // Add one month for age 62
  //       date = addMonths(date, 1)
  //     }
  //     return new Milestone({
  //       id: getISODateFromDate(date),
  //       label: label,
  //       date: date
  //     })
  //   })
  //   return options
  // }

  const getAmountHelp = (person: Person, start: string, amount: number) => {
    let help = ""
    // if (person && person.fullRetirementBenefit) {
    //   const frb = person.fullRetirementBenefit
    //   if (person.birthDate && start) {
    //     const birthDate = isoToLocalDate(person.birthDate)
    //     const startDate = isoToLocalDate(start)
    //     const benefit = SocialSecurityCalc.calculateRetirementBenefit(birthDate, startDate, frb, person.spouse)
    //     if (benefit.myBenefit === amount) {
    //       help = benefit.description
    //     } else {
    //       help = "Override Benefit"
    //     }
    //   }
    // }
    return help
  }

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      let change: PlanChange | undefined

      const benefits: ISocialSecurityBenefit[] = []
      if (startDate1 && person1) {
        benefits.push({personId: person1.id,
          fullBenefit: moneyToNumberFormat(fullRetirementBenefit1, 0),
          personalStart: startDate1,
          personalBenefit: amountDisable1 ? 0 : moneyToNumberFormat(amount1),
          spousalStart: spousalStart1,
          spousalBenefit: spousalStart1 ? moneyToNumberFormat(spousalBenefit1, 0) : 0,
          annualInf: percentToNumberFormat(annualInf, 2)
        })
      }
      if (startDate2 && person2) {
        benefits.push({personId: person2.id,
          fullBenefit: moneyToNumberFormat(fullRetirementBenefit2, 0),
          personalStart: startDate2,
          personalBenefit: amountDisable2 ? 0 : moneyToNumberFormat(amount2),
          spousalStart: spousalStart2,
          spousalBenefit: spousalStart2 ? moneyToNumberFormat(spousalBenefit2, 0) : 0,
          annualInf: percentToNumberFormat(annualInf, 2)
        })
      }
      const details: ISocialSecurityStrategyChange = {
        benefits: benefits,
        applyReduction: applyReduction
      }
      
      if (planChange && !planChange.id) {
        const input: CreatePlanChangeInput = {
          accountId: planChange?.accountId,
          userId: planChange.userId,
          modelId: planChange.modelId,
          planId: planChange.planId,
          changeType: planChange.changeType,
          name: planChange.name,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.createPlanChange(input)
        Tracking.event({action: "Social Security Strategy Created"})
      } else if (planChange) {
        const input: UpdatePlanChangeInput = {
          id: planChange.id,
          description: generateDescription(),
          enabled: !commitChanges,
          details: JSON.stringify(details)
        }
        change = await modelStore.updatePlanChange(input)
        Tracking.event({action: "Social Security Strategy Updated"})
      }

      if (change && commitChanges) {
        await change.commit(model, modelStore)
      }
      
      if (onSave && change) {
        onSave(plan, change)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const generateDescription = () => {
    let array: string[] = []
    if (person1) {
      if (startDate1 && amount1 !== "$0" && !amountDisable1) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(amount1), 0)
        array.push(`${person1!.nickname}: ${amount} at age ${ageToStringFormat(age1)}`)
      }
      if (spousalStart1 && spousalBenefit1) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(spousalBenefit1), 0)
        const age = person1.getAgeOnDate(isoToLocalDate(spousalStart1))
        array.push(`${person1!.nickname}: ${amount} at age ${ageToStringFormat(age)}`)
      }
    }
    if (person2) {
      if (startDate2 && amount2 !== "$0" && !amountDisable2) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(amount2), 0)
        array.push(`${person2!.nickname}: ${amount} at age ${ageToStringFormat(age2)}`)
      }
      if (spousalStart2 && spousalBenefit2) {
        const amount = numberToMoneyFormat(moneyToNumberFormat(spousalBenefit2), 0)
        const age = person2.getAgeOnDate(isoToLocalDate(spousalStart2))
        array.push(`${person2!.nickname}: ${amount} at age ${ageToStringFormat(age)}`)
      }
    }
    if (applyReduction) {
      array.push(`Apply projected benefit reduction`)
    }
    return array.join(", ")
  }


  const agetext = (value: number) => {
    const years = Math.floor(value / 12)
    const months = value % 12
    if (months === 0) {
      return String(years)
    } else {
      return `${years}, ${months}mo`
    }
  }

  const renderAgeSlider = (person: Person, start: string, num: number) => {
    const marks = [
      {value: 62*12, label:"62"},
      {value: 63*12, label:"63"},
      {value: 64*12, label:"64"},
      {value: 65*12, label:"65"},
      {value: 66*12, label:"66"},
      {value: 67*12, label:"67"},
      {value: 68*12, label:"68"},
      {value: 69*12, label:"69"},
      {value: 70*12, label:"70"},
    ]

    const birthDate = isoToLocalDate(person.birthDate)
    const birthDay = birthDate.getDate()
    let minMonths = 62*12
    if (birthDay > 2) {
      minMonths = 62*12+1
      marks[0] = {value: minMonths, label:"62"}
    }
    const fra = num === 1 ? fullRetirementAge1 : fullRetirementAge2
    const startDate = isoToLocalDate(start)
    const months = Math.max(Math.min(differenceInMonths(startDate, birthDate), 70*12), minMonths)

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" flexGrow={1}>
          <FormLabel sx={styles.formLabel}>{person.nickname}'s Start Age {`(${agetext(months)} on ${dateToLocalFormat(startDate)})`}</FormLabel>
          <FormLabel sx={styles.formLabel}>Full Retirement Age: {formatAge(fra)}</FormLabel>
        </Box>
        <Slider
          aria-label="Age slider"
          value={months}
          getAriaValueText={agetext}
          step={1}
          min={minMonths}
          max={70*12}
          marks={marks}
          size="small"
          color={num === 1 ? "primary" : "secondary"}
          valueLabelDisplay="auto"
          valueLabelFormat={agetext}
          sx={styles.slider}
          onChange={(event: Event, value: number | number[], activeThumb: number) => {
            // console.log(`onChangeSlider(${value}, ${activeThumb}`)
            const date = addMonths(birthDate, value as number)
            if (num === 1) {
              setStartDate1(getISODateFromDate(date))
            } else {
              setStartDate2(getISODateFromDate(date))
            }
          }}
        />
      </Box>
    )
  }

  if (!planChange) {
    return null
  }

  const commitDisabled = !(startDate1 || amount1 || startDate2 || amount2)
  const reductionEntry = SocialSecurityStrategyChange.reductionSchedule.entries().next()
  const reductionYear = reductionEntry.value[0]
  const reductionPercent = reductionEntry.value[1]
  const notice = persons.length > 1 ?
    <b>NOTICE: This strategy now utilizes your Full Retirement Age Benefits (PIA) to automatically estimate your personal and spouse benefits based on your start ages.</b> :
    <b>NOTICE: This strategy now utilizes your Full Retirement Age Benefit (PIA) to automatically estimate your benefits based on your start age.</b>

  return (
    <ModelEditDialog title="Social Security Strategy" open={isOpen} size="md" fullWidth={false}
                     onCancel={handleClose}
                     onSave={handleSave}
    >
      {/*<DialogContentText>*/}
      {/*  Adjust Social Security start dates and benefit amounts.*/}
      {/*</DialogContentText>*/}
      <DialogContentText>
        {notice}
      </DialogContentText>
      <DialogContentText>
        You can find this on your <Link href="https://www.ssa.gov/myaccount/statement.html" target="_blank" sx={styles.link}>
        Social Security Statement</Link>, or see <Link href="https://www.ssa.gov/OACT/anypia/index.html" target="_blank" sx={styles.link}>
        Social Security Benefit Calculators</Link>.
      </DialogContentText>
      <Grid container spacing={0}>
        {benefits.length > 0 &&
          <Grid item xs={12}>
             <SocialSecurityWidget benefits={benefits} persons={persons}
                                   annualInf={percentToNumberFormat(annualInf)}
                                   applyReduction={applyReduction}
                                   totalTax={totalTax}
                                   netLiquidAssets={netLiquidAssets}
                                   onMaximize={maximizeBenefits}
                                   isMaximizing={isMaximizing}
             />
          </Grid>
        }
        {person1 && startDate1 && [
          <Grid item xs={12} sm={8} sx={{pl:1, pr:1, pt:"14px"}}>
            {renderAgeSlider(person1, startDate1, 1)}
          </Grid>,
          <Grid item xs={12} sm={4} sx={{pl:2}}>
            <TextFieldValidator
              type="string"
              validators={{ required: false, minMoney: 0, isMoney: true }}
              name="fullRetirementBenefit1"
              variant="standard"
              margin="dense"
              fullWidth
              label={`${person1.nickname}'s Retirement Benefit (PIA)`}
              value={fullRetirementBenefit1}
              helperText="Enter in today's dollars"
              // helperText={`Full Retirement Age: ${formatAge(fullRetirementAge1)}`}
              InputLabelProps={{shrink: Boolean(fullRetirementBenefit1)}}
              onChange={(event: any) => {
                setFullRetirementBenefit1(event.target.value ?? 0)
                setIsLoading(false)
              }}
            />
          </Grid>
        ]}

        {person2 && startDate2 && [
          <Grid item xs={12} sm={8} sx={{pl:1, pr:1, pt:"14px"}}>
            {renderAgeSlider(person2, startDate2, 2)}
          </Grid>,
          <Grid item xs={12} sm={4} sx={{pl:2}}>
            <TextFieldValidator
              type="string"
              validators={{ required: false, minMoney: 0, isMoney: true }}
              name="fullRetirementBenefit2"
              variant="standard"
              margin="dense"
              fullWidth
              label={`${person2.nickname}'s Full Retirement Benefit (PIA)`}
              value={fullRetirementBenefit2}
              helperText="Enter in today's dollars"
              // helperText={`Full Retirement Age: ${formatAge(fullRetirementAge2)}`}
              InputLabelProps={{shrink: Boolean(fullRetirementBenefit2)}}
              onChange={(event: any) => {
                setFullRetirementBenefit2(event.target.value ?? 0)
                setIsLoading(false)
              }}
            />
          </Grid>
        ]}

        <Grid item xs={12} sm={8}>
          <FormControlLabel control={
            <Switch checked={applyReduction}
                    onChange={(event: any) => {
                      setApplyReduction(event.target.checked)
                    }}
            />}
            label={<div style={{display:"flex", alignItems:"center"}}>Apply projected {numberToPercentFormat(-reductionPercent, 0)} benefit reduction in {reductionYear}&nbsp;
              <a href="http://ssa.gov/ThereForMe" target="_blank"><LaunchIcon/></a></div>}
            componentsProps={{
              typography: {
                variant:"body2"
              }}
            }
            sx={{my:0, py:0, maxHeight:32}}
          />
          <FormControlLabel control={
            <Switch checked={commitChanges}
                      disabled={commitDisabled}
                      onChange={(event: any) => {
                        setCommitChanges(event.target.checked)
                      }}
            />}
            label={`Commit these updates to model to make them permanent.`}
            componentsProps={{
                typography: {
                  variant:"body2"
              }}
            }
            sx={{my:0, py:0, maxHeight:32}}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{pl:2}}>
          <TextFieldValidator
            type="string"
            validators={{ required: false, minValue: 0 }}
            name="annualInf"
            variant="standard"
            margin="dense"
            fullWidth
            label="Annual Inflation %"
            value={annualInf}
            onChange={(event: any) => setAnnualInf(event.target.value)}
          />
        </Grid>
      </Grid>
    </ModelEditDialog>
  )
}
export default SocialSecurityStrategyDialog